/**
 * GMA-1617 (CompassCare Web 2/14 — Overview) — client status grid.
 *
 * The Overview's scannable client roster (100+ clients). Filter chips
 * (CLIENT_FILTERS) scope the grid via the shared overviewModel.filterClients;
 * each client row shows a status dot, initials + name, a risk badge, living
 * environment, and last-update (webFormat). Selecting a client raises
 * onSelectClient so the parent can open the shared DetailDrawer.
 *
 * The filter is controlled by the parent (the KPI strip and these chips drive
 * the same activeFilter), so this component is presentational: given `clients`
 * + `activeFilter` it renders, and reports chip/row intent via callbacks.
 *
 * a11y: labelled region; a "Filter clients" group of pressed-state chips; the
 * roster is a <ul> whose rows stay listitems, each wrapping a real <button>
 * (no nested interactive element — the badge is a span). Fail-safe: null/empty
 * inputs render an empty (but labelled) roster; malformed rows use field
 * fallbacks. Colors from the single design-token set; `offline` (a connectivity
 * state, not a clinical severity) is rendered muted, distinct from severities.
 */
import React from "react";
import PropTypes from "prop-types";
import SeverityBadge from "./SeverityBadge";
import {
  filterClients,
  CLIENT_FILTERS,
  OFFLINE_STATUS,
} from "../../pages/web/overviewModel";
import { formatRelativeTime } from "../../utils/webFormat";
import { severityToken, theme } from "../../styles/designTokens";

const FILTER_LABELS = Object.freeze({
  all: "All",
  critical: "Critical",
  atRisk: "At risk", // match severity.atRisk.label (SeverityBadge) — one vocabulary
  caution: "Caution",
  offline: "Offline",
  groupHomes: "Group Homes",
  independent: "Independent",
});

const ENV_LABELS = Object.freeze({
  group_home: "Group Home",
  independent: "Independent",
});

// First letters of up to two name parts; "?" when the name is missing/blank.
function initials(name) {
  if (typeof name !== "string" || !name.trim()) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

export default function ClientGrid({
  clients,
  activeFilter = "all",
  onSelectFilter,
  onSelectClient,
}) {
  const filtered = filterClients(clients, activeFilter);
  // Distinguish "no data at all" from "the current filter matched nothing" so
  // the empty state isn't misleading.
  const hasAnyClients = Array.isArray(clients) && clients.some(Boolean);
  const chipsInteractive = typeof onSelectFilter === "function";
  const rowsInteractive = typeof onSelectClient === "function";

  return (
    <section
      data-testid="client-grid"
      aria-label="Client status"
      style={{ fontFamily: theme.fontUI, color: theme.textPrimary }}
    >
      <div
        role="group"
        aria-label="Filter clients"
        data-testid="client-grid-filters"
        style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}
      >
        {CLIENT_FILTERS.map((f) => {
          const active = f === activeFilter;
          return (
            <button
              key={f}
              type="button"
              data-testid={`client-filter-${f}`}
              aria-pressed={active}
              disabled={!chipsInteractive}
              onClick={chipsInteractive ? () => onSelectFilter(f) : undefined}
              style={{
                minHeight: 32,
                padding: "4px 12px",
                borderRadius: 16,
                border: `1px solid ${active ? theme.textPrimary : theme.border}`,
                background: active ? theme.surface : theme.bg,
                color: active ? theme.textPrimary : theme.textMuted,
                cursor: chipsInteractive ? "pointer" : "default",
                fontFamily: theme.fontUI,
                fontSize: 13,
              }}
            >
              {FILTER_LABELS[f] || f}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p
          data-testid="client-grid-empty"
          style={{ padding: 16, margin: 0, color: theme.textMuted }}
        >
          {hasAnyClients ? "No clients match this filter" : "No clients"}
        </p>
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 8,
          }}
        >
          {filtered.map((client, index) => {
            const offline = client.status === OFFLINE_STATUS;
            const dotColor = offline
              ? theme.textMuted
              : severityToken(client.status).color;
            const name = client.name || "Unknown client";
            const envLabel = ENV_LABELS[client.livingEnv] || null;
            return (
              <li key={client.id ?? `${name}-${index}`} data-testid="client-row">
                <button
                  type="button"
                  data-testid="client-open"
                  disabled={!rowsInteractive}
                  aria-label={`Open client ${name}`}
                  onClick={
                    rowsInteractive ? () => onSelectClient(client) : undefined
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    minHeight: 56,
                    padding: "8px 12px",
                    textAlign: "left",
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    background: theme.surface,
                    color: theme.textPrimary,
                    cursor: rowsInteractive ? "pointer" : "default",
                    fontFamily: theme.fontUI,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: dotColor,
                      flex: "0 0 auto",
                    }}
                  />
                  <span
                    aria-hidden="true"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: theme.bg,
                      color: theme.textMuted,
                      fontFamily: theme.fontMono,
                      fontSize: 12,
                      flex: "0 0 auto",
                    }}
                  >
                    {initials(client.name)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block" }}>{name}</span>
                    {envLabel ? (
                      <span style={{ fontSize: 12, color: theme.textMuted }}>
                        {envLabel}
                      </span>
                    ) : null}
                  </span>
                  {offline ? (
                    <span
                      data-testid="client-offline-badge"
                      style={{ fontSize: 12, color: theme.textMuted }}
                    >
                      Offline
                    </span>
                  ) : (
                    <SeverityBadge level={client.status} showDot={false} />
                  )}
                  <span
                    style={{
                      fontFamily: theme.fontMono,
                      fontSize: 11,
                      color: theme.textMuted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatRelativeTime(client.lastUpdate)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

ClientGrid.propTypes = {
  /** Clients; { id, name, status, livingEnv, lastUpdate }. Fail-safe for null/rows. */
  clients: PropTypes.array,
  /** Active CLIENT_FILTERS key; defaults to "all". */
  activeFilter: PropTypes.string,
  /** Called with a filter key when a chip is clicked. */
  onSelectFilter: PropTypes.func,
  /** Called with the client when a row is opened (parent opens the DetailDrawer). */
  onSelectClient: PropTypes.func,
};
